import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
Container,
Typography,
Paper,
List,
ListItem,
ListItemText,
ListItemAvatar,
Avatar,
Divider,
Chip,
Button,
Modal,
Box,
TextField,
IconButton,
useMediaQuery,
useTheme,
Fade,
Slide,
Grow,
Badge,
Stack,
Grid,
InputAdornment,
FormControl,
Select,
InputLabel,
MenuItem,
Card,
CardContent,
CardActions,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
CircularProgress,
Tooltip,
Alert,
Snackbar
} from '@mui/material';
import {
School as SchoolIcon,
Close as CloseIcon,
CheckCircle as CheckCircleIcon,
Search,
Refresh,
Add as AddIcon,
Person as PersonIcon,
DateRange as DateRangeIcon,
Delete as DeleteIcon,
Edit as EditIcon
} from '@mui/icons-material';
import NavigationMenu from '../navigation/NavigationMenu';
import useCustomToast from '../../hooks/useToast';
import axios from 'axios';
import { headers } from '../../utilities/Env';
import { APIUniversitiesList, APIUniversityStore } from '../../utilities/APIS';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../utilities/Text';
import LoadingState from '../../components/LoadingState';
import { format } from 'date-fns';

const Institutions = () => {
    const [universitiesData, setUniversitiesData] = useState([]);
    const showToast = useCustomToast();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    
    // Get logged in user from sessionStorage
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    
    // Add Affiliations Dialog State
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [newAffiliation, setNewAffiliation] = useState({
        name: '',
        creator: ''
    });
    const [isAdding, setIsAdding] = useState(false);
    const [formErrors, setFormErrors] = useState({
        name: '',
        creator: ''
    });

    useEffect(() => {
        // Check if user is logged in
        const user = sessionStorage.getItem('reseacher');
        if (user) {
            const parsedUser = JSON.parse(user);
            setLoggedInUser(parsedUser);
            setIsLoggedIn(parsedUser.loggedIn === true);
        }
        fetchData();
    }, []);

    useEffect(() => {
        filterAndSortData();
    }, [universitiesData, searchTerm, sortOrder]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [uniRes] = await Promise.all([
                axios.get(APIUniversitiesList, { headers }),
            ]);

            const res = Array.isArray(uniRes.data.data) ? uniRes.data.data.reverse() : [];
            setUniversitiesData(res);
            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";

            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            }
            else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }

            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortData = () => {
        let filtered = universitiesData;

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(affiliation =>
                affiliation.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                affiliation.creator?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort data
        filtered = [...filtered].sort((a, b) => {
            let aValue = a.name?.toLowerCase() || '';
            let bValue = b.name?.toLowerCase() || '';

            if (sortOrder === 'asc') {
                if (aValue < bValue) return -1;
                if (aValue > bValue) return 1;
                return 0;
            } else {
                if (aValue > bValue) return -1;
                if (aValue < bValue) return 1;
                return 0;
            }
        });

        setFilteredData(filtered);
    };

    const validateForm = () => {
        let isValid = true;
        const errors = { name: '', creator: '' };

        if (!newAffiliation.name.trim()) {
            errors.name = 'Affiliation name is required';
            isValid = false;
        } else if (newAffiliation.name.length < 3) {
            errors.name = 'Affiliation name must be at least 3 characters';
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    const handleAddAffiliation = async () => {
        if (!validateForm()) {
            return;
        }

        setIsAdding(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', newAffiliation.name.trim());
            formDataToSend.append('creator', newAffiliation.creator.trim());

            const response = await axios.post(APIUniversityStore, formDataToSend, { headers });

            if (response.data.status === "success") {
                showToast("Success", "Affiliation added successfully!", "success");
                // Refresh the list
                await fetchData();
                // Close dialog and reset form
                setOpenAddDialog(false);
                setNewAffiliation({ name: '', creator: loggedInUser?.name || '' });
                setFormErrors({ name: '', creator: '' });
            } else {
                showToast("Error", response.data.message || "Failed to add affiliation", "error");
            }
        } catch (error) {
            console.error("Error adding affiliation:", error);
            if (error.response) {
                if (error.response.status === 422) {
                    const errors = error.response.data.errors;
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(...errors[field]);
                    }
                    showToast("Validation Error", errorMessages.join("\n"), "error");
                } else {
                    showToast("Error", error.response.data.message || "An error occurred while adding affiliation", "error");
                }
            } else if (error.code === "ERR_NETWORK") {
                showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
            } else {
                showToast("Error", "An unexpected error occurred", "error");
            }
        } finally {
            setIsAdding(false);
        }
    };

    const handleInputChange = (field, value) => {
        setNewAffiliation(prev => ({
            ...prev,
            [field]: value
        }));
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return format(date, 'PPP'); // Returns format like "Jan 1, 2024"
        } catch (error) {
            return 'Invalid Date';
        }
    };

    if (error) {
        return (
            <>
                <NavigationMenu />
                <Container maxWidth="lg" sx={{ mt: 4 }}>
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                </Container>
            </>
        );
    }

    if (loading) {
        return (
            <>
                <NavigationMenu />
                <LoadingState />
            </>
        );
    }

    return (
        <div className='body-container'>
            <NavigationMenu />
            <br />
            <br />
            <br />

            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                    <Box sx={{
                        mb: 3,
                        p: 3,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2
                    }}>
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Registered Affiliations
                            </Typography>
                            <Typography variant="subtitle1">
                                A comprehensive list of all affiliations registered in the system
                            </Typography>
                        </Box>
                        {/* Show Add button only if user is logged in */}
                        {isLoggedIn && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    // Set creator to logged in user's name when opening dialog
                                    setNewAffiliation({
                                        name: '',
                                        creator: loggedInUser?.name || ''
                                    });
                                    setOpenAddDialog(true);
                                }}
                                sx={{
                                    bgcolor: 'white',
                                    color: '#667eea',
                                    '&:hover': {
                                        bgcolor: '#f0f0f0',
                                        transform: 'translateY(-2px)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Add Affiliation
                            </Button>
                        )}
                    </Box>
                </Slide>

                {/* Search and Filter Section */}
                <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <TextField
                                fullWidth
                                placeholder="Search by name or creator..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search color="primary" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                <CloseIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Sort Order</InputLabel>
                                <Select
                                    value={sortOrder}
                                    label="Sort Order"
                                    onChange={(e) => setSortOrder(e.target.value)}
                                >
                                    <MenuItem value="asc">A to Z</MenuItem>
                                    <MenuItem value="desc">Z to A</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="outlined"
                                onClick={fetchData}
                                startIcon={<Refresh />}
                                fullWidth
                                sx={{ height: '56px' }}
                            >
                                Refresh
                            </Button>
                        </Grid>
                    </Grid>
                    <br />
                    
                    {/* Results Count */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="h6" color="text.secondary">
                            Showing {filteredData.length} of {universitiesData.length} Affiliations
                        </Typography>
                        {searchTerm && (
                            <Chip 
                                label={`Search: "${searchTerm}"`} 
                                onDelete={() => setSearchTerm('')}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Box>
                </Paper>

                {/* Affiliations List - Keeping the old list style */}
                <Grow in={true} timeout={800}>
                    <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                            {filteredData.length === 0 ? (
                                <Box sx={{ p: 6, textAlign: 'center' }}>
                                    <SchoolIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="h5" color="text.secondary" gutterBottom>
                                        No Affiliations Found
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                        {searchTerm ? 'Try adjusting your search criteria' : 'No affiliations available'}
                                    </Typography>
                                    {!searchTerm && isLoggedIn && (
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                setNewAffiliation({
                                                    name: '',
                                                    creator: loggedInUser?.name || ''
                                                });
                                                setOpenAddDialog(true);
                                            }}
                                        >
                                            Add Your First Affiliation
                                        </Button>
                                    )}
                                </Box>
                            ) : (
                                filteredData.map((item, index) => (
                                    <React.Fragment key={item.id}>
                                        <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                                    {index + 1}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h6" component="div">
                                                        {item.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                                                        <Chip
                                                            size="small"
                                                            icon={<PersonIcon fontSize="small" />}
                                                            label={`Creator: ${item.creator || 'Ria Admin'}`}
                                                            variant="outlined"
                                                            sx={{ borderRadius: 2 }}
                                                        />
                                                        {item.created_at && (
                                                            <Chip
                                                                size="small"
                                                                icon={<DateRangeIcon fontSize="small" />}
                                                                label={`Added: ${formatDate(item.created_at)}`}
                                                                variant="outlined"
                                                                sx={{ borderRadius: 2 }}
                                                            />
                                                        )}
                
                                                        <Chip
                                                            label="Active"
                                                            color="success"
                                                            size="small"
                                                            icon={<CheckCircleIcon fontSize="small" />}
                                                            sx={{ borderRadius: 2 }}
                                                        />
                                                    </Stack>
                                                }
                                            />
                                            <Tooltip title="Registered Affiliation">
                                                <Badge 
                                                    badgeContent="Verified" 
                                                    color="success" 
                                                    sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
                                                >
                                                    <SchoolIcon color="primary" sx={{ fontSize: 30, opacity: 0.7 }} />
                                                </Badge>
                                            </Tooltip>
                                        </ListItem>
                                        {index < filteredData.length - 1 && <Divider variant="inset" component="li" />}
                                    </React.Fragment>
                                ))
                            )}
                        </List>
                    </Paper>
                </Grow>
            </Container>

            {/* Add Affiliation Dialog */}
            <Dialog 
                open={openAddDialog} 
                onClose={() => !isAdding && setOpenAddDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                            <SchoolIcon color="primary" fontSize="large" />
                            Add New Affiliation
                        </Typography>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => setOpenAddDialog(false)}
                            disabled={isAdding}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Please enter the affiliation details below. The creator name is automatically set based on your login information.
                        </Typography>
                        
                        <TextField
                            autoFocus
                            label="Affiliation Name *"
                            fullWidth
                            value={newAffiliation.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            disabled={isAdding}
                            placeholder="e.g., University of Dar es Salaam, Research Institute, etc."
                            helperText={formErrors.name || "Enter the full official name of the institution/organization"}
                            error={!!formErrors.name}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SchoolIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        <TextField
                            label="Creator Name *"
                            fullWidth
                            value={newAffiliation.creator}
                            disabled={true} // Make it non-editable
                            placeholder="Creator name (auto-filled from login)"
                            helperText="This is automatically set from your logged-in account"
                            InputProps={{
                                readOnly: true,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: '#f5f5f5',
                                }
                            }}
                        />
                        
                        {/* Display logged in user info */}
                        {loggedInUser && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Adding affiliation as: <strong>{loggedInUser.name}</strong>
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button 
                        onClick={() => setOpenAddDialog(false)} 
                        disabled={isAdding}
                        startIcon={<CloseIcon />}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAddAffiliation} 
                        variant="contained" 
                        color="primary"
                        disabled={isAdding || !newAffiliation.name.trim()}
                        startIcon={isAdding ? <CircularProgress size={20} /> : <AddIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46a0 100%)',
                            }
                        }}
                    >
                        {isAdding ? 'Adding...' : 'Add Affiliation'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Institutions;